import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import PropTypes from "prop-types";
import PasskeysHeader from "./components/PasskeysHeader";
import PasskeysList from "./components/PasskeysList";
import DeleteModal from "./components/DeleteModal";
import { PASSKEY_CONSTANTS } from "../../utils/passkeyUtils";
import {
  usePasskeyFetcher,
  usePasskeyAddOperation,
  usePasskeyDeleteOperation,
  useAuthentication,
} from "../../hooks/passkeys";
import { Spinner } from "react-bootstrap";

const PasskeysSection = ({
  onShowToast,
  accessToken,
  userId,
  ngcmfaExpiry,
}) => {
  const { instance } = useMsal();
  const [showOverlaySpinner, setShowOverlaySpinner] = useState(false);
  const maxPasskeys = PASSKEY_CONSTANTS.MAX_PASSKEYS;

  // Custom hooks handle all the complex logic
  const { passkeys, isLoading, error, fetchPasskeys } = usePasskeyFetcher({
    accessToken,
    userId,
    onShowToast,
  });

  const { handleAddPasskey, performAddPasskey } = usePasskeyAddOperation({
    accessToken,
    userId,
    ngcmfaExpiry,
    onShowToast,
    fetchPasskeys,
    currentPasskeys: passkeys,
  });

  const {
    initiate: initiateDelete,
    showConfirmationModal,
    modalProps,
  } = usePasskeyDeleteOperation({
    accessToken,
    userId,
    ngcmfaExpiry,
    onShowToast,
    fetchPasskeys,
    currentPasskeys: passkeys,
    setShowOverlaySpinner
  });

  const { getCachedOperation, clearCachedOperation } = useAuthentication({
    onShowToast,
  });

  // Handle initial fetch
  useEffect(() => {
    if (accessToken && userId) {
      fetchPasskeys().catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, userId]); // Only depend on accessToken and userId, not fetchPasskeys

  useEffect(() => {
    instance
      .handleRedirectPromise()
      .then((response) => {
        const operation = getCachedOperation();

        if (operation && accessToken && userId) {
          clearCachedOperation();

          if (operation.action === "add") {
            performAddPasskey();
          } else if (operation.action === "delete" && operation.passkey) {
            showConfirmationModal(operation.passkey);
          }
        }
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, userId]);

  return (
    <>
      <section className="passkeys-section mb-4">
        <PasskeysHeader
          count={passkeys.length}
          maxCount={maxPasskeys}
          onAddClick={async () => {
            setShowOverlaySpinner(true);
            try {
              await handleAddPasskey();
            } catch {
              // do nothing
            }
            setShowOverlaySpinner(false);
          }}
          isLoading={isLoading}
        />
        <PasskeysList
          passkeys={passkeys}
          onDelete={initiateDelete}
          isLoading={isLoading}
          error={error}
        />
      </section>

      <DeleteModal {...modalProps} />

      {/* Spinner overlay - START */}
      {showOverlaySpinner && (
        <div
          style={{
            width: "100vw",
            height: "100vh",
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 5,
            background: "rgba(255,255,255,.5)",
            textAlign: "center",
            paddingTop: "45vh",
          }}
        >
          <Spinner
            animation="border"
            role="status"
            className="mb-3"
            variant="primary"
          >
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}
      {/* Spinner overlay - END */}
    </>
  );
};

PasskeysSection.propTypes = {
  onShowToast: PropTypes.func.isRequired,
  accessToken: PropTypes.string,
  userId: PropTypes.string,
  ngcmfaExpiry: PropTypes.number,
};

export default PasskeysSection;
